import type {
  LinkDismissal,
  LinkSignal,
  Market,
  User,
} from '../dataProvider/types';

/**
 * The Ban-evasion query/aggregation module (D-ban-evasion) - the ONE place suspected
 * same-person links are DERIVED. Same seam idiom as the fraud queue / suspensions /
 * referrals: v1 aggregates client-side from the mock provider, every selector lives
 * here, and the late real-data migration swaps this module for a server-side
 * endpoint without the surface changing.
 *
 * THE MODEL. Ban-evasion is one person operating multiple accounts to evade a ban,
 * so it is a RELATIONSHIP between accounts, NOT a single-user ranking. The surface is
 * anchored on BANNED accounts: for each banned user it finds ACTIVE users that share
 * an identity signal, and surfaces them as suspected links beneath the anchor (the
 * referrals edge-between-users idiom applied to the banned anchor).
 *
 * THE SIGNALS. Detection matches on the modelled identity signals (deviceId /
 * ipAddress / signupAddress, engine-written stand-ins) PLUS the one signal that
 * already exists, the referral cluster (referredBy). It deliberately does NOT match
 * on billingReference or payoutDetails: those are subscription / payout artefacts,
 * not identity, and matching on them is the proxy-because-it-exists trap.
 *
 * STRENGTH IS THE BASIS, NOT A SCORE. A link records WHICH signals it matched on and
 * ranks by how many. A multi-signal link (device + IP + address) ranks above a
 * single-signal one (address only). The surface shows the matched signals so the
 * reviewer sees WHY two accounts are linked (a shared address reads "possibly a
 * household, judge it"; device + IP reads "investigate now"), never an opaque score.
 *
 * INVESTIGATIVE-GLOBAL, inherited from the fraud queue / suspensions: the match runs
 * CROSS-MARKET (a banned UK account evading via a US account is exactly the case), so
 * it never scopes to one market. The optional region facet filters the ANCHOR's
 * market by exact value (not marketsForScope, not the switcher).
 */

/** Display order and labels are derived from this canonical list. */
export const LINK_SIGNAL_LABEL: Record<LinkSignal, string> = {
  deviceId: 'Shared device',
  ipAddress: 'Shared IP',
  signupAddress: 'Shared address',
  referral_cluster: 'Referral cluster',
};

export interface SuspectedLink {
  linkedId: number;
  linkedName: string;
  linkedEmail: string;
  linkedMarket: Market;
  linkedCreatedAt: string;
  /** The previous fraud state of the linked account (always active when surfaced),
   *  carried for the inherited ban action's audit diff. */
  linkedFraudReason: string | null;
  linkedSuspensionReasonCode: User['suspensionReasonCode'];
  linkedAppealStatus: User['appealStatus'];
  /** The linked account's current dismissals, carried so the dismiss action can
   *  append to them through the chokepoint. */
  linkedDismissals: LinkDismissal[];
  /** The matched basis: which signals link the two accounts. */
  signals: LinkSignal[];
  /** Link strength = number of matched signals. Not a score, just the count. */
  strength: number;
  /** True when the linked account was created AFTER the anchor's ban-era createdAt,
   *  the evasion pattern. Surfaced as a flag, never a filter. */
  createdAfterAnchor: boolean;
}

export interface BanEvasionAnchor {
  anchorId: number;
  anchorName: string;
  anchorEmail: string;
  anchorMarket: Market;
  anchorReason: string | null;
  links: SuspectedLink[];
}

export interface BanEvasionView {
  anchors: BanEvasionAnchor[];
}

export interface BanEvasionInput {
  users: User[];
}

export interface BanEvasionOptions {
  /** Exact-value market facet on the ANCHOR (uk / us), or null for the global view. */
  regionFilter?: Market | null;
}

/** The canonical signal order, so chips and comparisons are deterministic. */
const SIGNAL_ORDER: readonly LinkSignal[] = [
  'deviceId',
  'ipAddress',
  'signupAddress',
  'referral_cluster',
];

/** Do the two accounts sit in the same referral cluster? Same referrer, or one
 *  refers the other. */
const sameReferralCluster = (a: User, b: User): boolean =>
  (a.referredBy != null && a.referredBy === b.referredBy) ||
  a.referredBy === b.id ||
  b.referredBy === a.id;

/** The identity + relationship signals the two accounts share, in canonical order. */
const matchedSignals = (anchor: User, candidate: User): LinkSignal[] => {
  const signals: LinkSignal[] = [];
  if (anchor.deviceId != null && anchor.deviceId === candidate.deviceId) {
    signals.push('deviceId');
  }
  if (anchor.ipAddress != null && anchor.ipAddress === candidate.ipAddress) {
    signals.push('ipAddress');
  }
  if (
    anchor.signupAddress != null &&
    anchor.signupAddress === candidate.signupAddress
  ) {
    signals.push('signupAddress');
  }
  if (sameReferralCluster(anchor, candidate)) {
    signals.push('referral_cluster');
  }
  return signals;
};

/**
 * Is this link suppressed by a prior dismissal? A dismissal covers the link only
 * while the CURRENT matched set is a subset of (some) dismissed-at set. A later
 * STRONGER match (a signal beyond every dismissal) RE-RAISES the link.
 */
const isDismissed = (
  current: LinkSignal[],
  dismissals: LinkDismissal[],
  anchorId: number,
): boolean =>
  dismissals.some(
    (d) =>
      d.anchorId === anchorId && current.every((s) => d.signals.includes(s)),
  );

const byStrengthThenName = (a: SuspectedLink, b: SuspectedLink): number =>
  b.strength - a.strength ||
  Number(b.createdAfterAnchor) - Number(a.createdAfterAnchor) ||
  a.linkedName.localeCompare(b.linkedName);

export function buildBanEvasionView(
  input: BanEvasionInput,
  opts: BanEvasionOptions = {},
): BanEvasionView {
  const banned = input.users.filter((u) => u.fraudStatus === 'banned');
  const active = input.users.filter((u) => u.fraudStatus === 'active');

  const anchors: BanEvasionAnchor[] = [];
  for (const anchor of banned) {
    if (opts.regionFilter != null && anchor.market !== opts.regionFilter) {
      continue;
    }
    const links: SuspectedLink[] = [];
    for (const candidate of active) {
      const signals = matchedSignals(anchor, candidate);
      if (signals.length === 0) continue;
      if (isDismissed(signals, candidate.linkDismissals, anchor.id)) continue;
      links.push({
        linkedId: candidate.id,
        linkedName: candidate.name,
        linkedEmail: candidate.email,
        linkedMarket: candidate.market,
        linkedCreatedAt: candidate.createdAt,
        linkedFraudReason: candidate.fraudReason,
        linkedSuspensionReasonCode: candidate.suspensionReasonCode,
        linkedAppealStatus: candidate.appealStatus,
        linkedDismissals: candidate.linkDismissals,
        signals: [...signals].sort(
          (a, b) => SIGNAL_ORDER.indexOf(a) - SIGNAL_ORDER.indexOf(b),
        ),
        strength: signals.length,
        createdAfterAnchor: candidate.createdAt > anchor.createdAt,
      });
    }
    if (links.length === 0) continue;
    links.sort(byStrengthThenName);
    anchors.push({
      anchorId: anchor.id,
      anchorName: anchor.name,
      anchorEmail: anchor.email,
      anchorMarket: anchor.market,
      anchorReason: anchor.fraudReason,
      links,
    });
  }

  // Anchors with the strongest single link first, then by name.
  anchors.sort(
    (a, b) =>
      (b.links[0]?.strength ?? 0) - (a.links[0]?.strength ?? 0) ||
      a.anchorName.localeCompare(b.anchorName),
  );

  return { anchors };
}
